import {
  Component,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Chart, ChartOptions, ChartType, Color } from 'chart.js';

@Component({
  selector: 'app-chart-wrapper',
  templateUrl: './chart-wrapper.component.html',
  styleUrls: ['./chart-wrapper.component.scss']
})
export class ChartWrapperComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() chartData: any; // The data for the chart (this will be passed to the component)
  @Input() chartOptions: ChartOptions = {}; // The options for the chart
  @Input() chartType: ChartType = 'bar'; // The type of chart (line, bar, pie, etc.)
  @Input() height: string = '300px'; // Height of the chart container
  @Input() responsive: boolean = true; // Whether the chart should be responsive
  chart: Chart | undefined; // The chart instance

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    // You can do any initialization here if needed
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If chartData changes, recreate the chart
    if (changes['chartData'] && this.chart) {
      console.log('Chart data changed, updating chart...');
      this.chart.data = this.chartData; // Update the chart data
      this.chart.update(); // Redraw the chart with new data
    }

    // If chartType changes, recreate the chart
    if (changes['chartType'] && this.chart) {
      console.log('Chart type changed, recreating chart...');
      this.destroyChart();
      setTimeout(() => this.createChart(), 100);
    }
  }

  ngAfterViewInit(): void {
    // Delay chart creation to ensure DOM is ready
    setTimeout(() => {
      this.createChart();
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private destroyChart(): void {
    // Clean up chart when the component is destroyed to avoid memory leaks
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  createChart(): void {
    // Ensure the chart gets created only once the DOM is available
    const canvas = this.el.nativeElement.querySelector('canvas') as HTMLCanvasElement;
    if (!canvas) {
      console.error('Canvas element not found');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get 2D context');
      return;
    }

    // Destroy existing chart if it exists
    if (this.chart) {
      this.chart.destroy();
    }

    // Default options for better chart appearance
    const isBarChart = this.chartType === 'bar';

    const defaultOptions: ChartOptions = {
      responsive: this.responsive,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true
        }
      },
      scales: isBarChart
        ? {
            x: {
              grid: {
                display: false
              },
              ticks: {
                font: {
                  size: 12
                }
              }
            },
            y: {
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              },
              ticks: {
                font: {
                  size: 12
                }
              }
            }
          }
        : {}
    };

    // For pie/doughnut with multiple datasets, override legend and tooltip to treat
    // all slices as one flat list keyed by chartData.labels.
    const isPieType = this.chartType === 'pie' || this.chartType === 'doughnut';
    const hasMultipleDatasets = (this.chartData?.datasets?.length ?? 0) > 1;

    if (isPieType && hasMultipleDatasets) {
      const resolveSlice = (chart: Chart, flatIndex: number) => {
        const lengths = chart.data.datasets.map((ds) => ds.data.length);
        let runningTotal = 0;
        let datasetIndex = 0;
        for (let i = 0; i < lengths.length; i++) {
          runningTotal += lengths[i];
          if (flatIndex < runningTotal) { datasetIndex = i; break; }
        }
        const sliceIndex = flatIndex - (runningTotal - lengths[datasetIndex]);
        return { datasetIndex, sliceIndex };
      };

      this.chartOptions.plugins = {
        ...this.chartOptions.plugins,
        legend: {
          labels: {
            generateLabels: function (chart) {
              const original = Chart.overrides.pie.plugins.legend.labels.generateLabels;
              const labelsOriginal = original.call(this, chart);

              labelsOriginal.forEach((label) => {
                const { datasetIndex, sliceIndex } = resolveSlice(chart, label.index ?? 0);
                label.datasetIndex = datasetIndex;

                const meta = chart.getDatasetMeta(datasetIndex);
                label.hidden = !!(meta.data[sliceIndex] as any)?.hidden;

                const bg = chart.data.datasets[datasetIndex].backgroundColor as any[];
                label.fillStyle = bg[sliceIndex];

                const value = (chart.data.datasets[datasetIndex].data as number[])[sliceIndex];
                if (value !== undefined) {
                  label.text = `${label.text}  (${value})`;
                }
              });

              return labelsOriginal;
            }
          },
          onClick: function (_e: any, legendItem: any, legend: any) {
            const chart = legend.chart;
            const { datasetIndex, sliceIndex } = resolveSlice(chart, legendItem.index ?? 0);
            const meta = chart.getDatasetMeta(datasetIndex);
            if (meta.data[sliceIndex]) {
              (meta.data[sliceIndex] as any).hidden = !(meta.data[sliceIndex] as any).hidden;
            }
            chart.update();
          }
        },
        tooltip: {
          callbacks: {
            title: function (context) {
              const datasetLengths = context[0].chart.data.datasets.map((ds) => ds.data.length);
              let offset = 0;
              for (let i = 0; i < context[0].datasetIndex; i++) {
                offset += datasetLengths[i];
              }
              const labelIndex = offset + context[0].dataIndex;
              const label = context[0].chart?.data?.labels?.[labelIndex] || 'Unknown';
              return label + ': ' + context[0].formattedValue;
            }
          }
        }
      };
    }

    this.chart = new Chart(ctx, {
      type: this.chartType,
      data: this.chartData || { labels: [], datasets: [] },
      options: {
        ...defaultOptions,
        ...this.chartOptions,
        plugins: {
          ...defaultOptions.plugins,
          ...this.chartOptions.plugins
        }
      }
    });

    // Trigger resize event to ensure proper rendering
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  // Method to update chart data
  updateChartData(newData: any): void {
    if (this.chart) {
      this.chart.data = newData;
      this.chart.update();
    }
  }

  // Method to resize chart
  resizeChart(): void {
    if (this.chart) {
      this.chart.resize();
    }
  }
}
